import { UserRole } from '../../types/user';

/**
 * Backend API role type
 * The backend uses American spelling "traveler" in API paths and request/response bodies
 */
export type BackendRoleType = 'sender' | 'traveler';

/**
 * Converts frontend UserRole enum to backend API string format
 * Frontend uses British spelling "traveller", backend uses American "traveler"
 */
export const roleToBackendString = (role: UserRole): BackendRoleType => {
  switch (role) {
    case UserRole.SENDER:
      return 'sender';
    case UserRole.TRAVELLER:
      return 'traveler';
    default:
      return 'sender';
  }
};

/**
 * Converts backend API string to frontend UserRole enum
 */
export const backendStringToRole = (backendRole: BackendRoleType): UserRole => {
  return backendRole === 'traveler' ? UserRole.TRAVELLER : UserRole.SENDER;
};

/**
 * Type guard to check if a value is a valid backend role string
 */
export const isBackendRole = (value: unknown): value is BackendRoleType => {
  return value === 'sender' || value === 'traveler';
};

/**
 * Type guard to check if a value is a valid UserRole
 */
export const isUserRole = (value: unknown): value is UserRole => {
  return value === UserRole.SENDER || value === UserRole.TRAVELLER || value === UserRole.NONE;
};

/**
 * Type guard to check if a role is sender
 */
export const isSenderRole = (role: UserRole | undefined | null): role is UserRole => {
  return role === UserRole.SENDER;
};

/**
 * Type guard to check if a role is traveller.
 * Checks !isSenderRole instead of exact string match to handle both
 * 'traveller' (frontend) and 'traveler' (backend) spellings.
 */
export const isTravellerRole = (role: UserRole | string | undefined | null): boolean => {
  if (!role) return false;
  return !isSenderRole(role as UserRole);
};
